import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Coins, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const POLL_INTERVAL = 2000;
const POLL_ATTEMPTS = 10;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", record: null, attempts: 0 });
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          stopped.current = true;
          setState({ status: "paid", record: data, attempts });
          try { await refresh(); } catch (e) {
            // Non-fatal: user is refreshed in AuthContext on next mount.
            console.warn("[payment] auth refresh failed:", e);
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          stopped.current = true;
          setState({ status: "failed", record: data, attempts });
          return;
        }
        if (attempts >= POLL_ATTEMPTS) {
          stopped.current = true;
          setState({ status: "timeout", record: data, attempts });
          return;
        }
        setState({ status: "polling", record: data, attempts });
        if (!stopped.current) setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        console.warn("[payment] status poll failed:", e);
        stopped.current = true;
        setState({ status: "error", record: null, attempts });
      }
    };
    poll();
    return () => { stopped.current = true; };
  }, [sessionId, refresh]);

  const view = () => {
    if (!sessionId) {
      return (
        <>
          <XCircle size={32} className="text-rose-500 mb-4" />
          <h1 className="font-display text-3xl mb-2">Missing session.</h1>
          <p className="text-sm text-zinc-400">No session_id in the URL.</p>
        </>
      );
    }
    if (state.status === "paid") {
      return (
        <>
          <CheckCircle2 size={32} className="text-emerald-400 mb-4" />
          <h1 className="font-display text-3xl mb-2">Payment complete.</h1>
          <p className="text-sm text-zinc-400 mb-6">
            {state.record?.coins?.toLocaleString()} Panda Coins have been credited to your wallet.
          </p>
          <div className="flex items-center gap-3 justify-center">
            <Button onClick={() => navigate("/feed")} className="btn-crimson rounded-none px-6">
              Back to the vault
            </Button>
            <Link to="/coins" className="text-sm text-zinc-500 hover:text-white transition-colors">
              Buy more
            </Link>
          </div>
        </>
      );
    }
    if (state.status === "failed") {
      return (
        <>
          <XCircle size={32} className="text-rose-500 mb-4" />
          <h1 className="font-display text-3xl mb-2">Payment failed.</h1>
          <p className="text-sm text-zinc-400 mb-6">
            Your card was declined or the payment expired. No coins were added.
          </p>
          <Link to="/coins">
            <Button className="btn-crimson rounded-none px-6">Try again</Button>
          </Link>
        </>
      );
    }
    if (state.status === "timeout") {
      return (
        <>
          <Loader2 size={32} className="text-zinc-400 mb-4 animate-spin" />
          <h1 className="font-display text-3xl mb-2">Still processing…</h1>
          <p className="text-sm text-zinc-400 mb-6">
            Stripe is taking longer than expected. Your coins will land in the wallet as soon as
            the payment clears — you can safely close this page.
          </p>
          <Link to="/feed">
            <Button className="btn-crimson rounded-none px-6">Back to feed</Button>
          </Link>
        </>
      );
    }
    if (state.status === "error") {
      return (
        <>
          <XCircle size={32} className="text-rose-500 mb-4" />
          <h1 className="font-display text-3xl mb-2">Couldn't check status.</h1>
          <p className="text-sm text-zinc-400">Please contact support with your session ID.</p>
        </>
      );
    }
    return (
      <>
        <Loader2 size={32} className="text-amber-400 mb-4 animate-spin" />
        <h1 className="font-display text-3xl mb-2">Confirming payment…</h1>
        <p className="text-sm text-zinc-400">
          Checking with Stripe. Attempt {state.attempts} of {POLL_ATTEMPTS}.
        </p>
      </>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16">
      <div
        className="card-surface p-10 max-w-lg w-full text-center flex flex-col items-center"
        data-testid="payment-success-root"
      >
        <Coins size={22} className="text-amber-400 absolute mt-[-32px]" />
        {view()}
      </div>
    </div>
  );
}
